// controllers/recruitmentController.js
import apiService from '../services/apiService';
import Recruitment from '../models/Recruitment';

export const recruitmentController = {
  /**
   * Get all job postings with filters
   * @param {Object} filters - { page, limit, status, department, jobType }
   * @returns {Promise<Object>} - { success, data, total, pagination }
   */
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `recruitment?${queryParams}` : 'recruitment';
      const response = await apiService.get(endpoint);
      
      const items = response.data?.map(item => Recruitment.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: response.total || items.length,
        pagination: response.pagination || null
      };
    } catch (error) {
      console.error('Error fetching jobs:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to fetch job postings' 
      };
    }
  },

  /**
   * Get single job posting by ID
   * @param {string} id - Job ID
   * @returns {Promise<Object>} - { success, data }
   */
  getById: async (id) => {
    try {
      const response = await apiService.get(`recruitment/${id}`);
      return { 
        success: true, 
        data: Recruitment.fromJSON(response.data) 
      };
    } catch (error) {
      console.error('Error fetching job:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message 
      };
    }
  },

  /**
   * Create new job posting
   * @param {Object} data - Job data
   * @returns {Promise<Object>} - { success, data, message }
   */
  create: async (data) => {
    try {
      const job = new Recruitment(data);
      const validation = job.validate();
      
      if (!validation.isValid) {
        return { 
          success: false, 
          errors: validation.errors,
          message: 'Validation failed'
        };
      }
      
      const response = await apiService.post('recruitment', job.toJSON());
      
      return { 
        success: true, 
        data: Recruitment.fromJSON(response.data), 
        message: response.message || 'Job posting created successfully' 
      };
    } catch (error) {
      console.error('Error creating job:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to create job posting' 
      };
    }
  },

  /**
   * Update job posting
   * @param {string} id - Job ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} - { success, data, message }
   */
  update: async (id, data) => {
    try {
      const job = new Recruitment({ ...data, id });
      const validation = job.validate();
      
      if (!validation.isValid) {
        return { 
          success: false, 
          errors: validation.errors 
        };
      }
      
      const response = await apiService.put(`recruitment/${id}`, job.toJSON());
      
      return { 
        success: true, 
        data: Recruitment.fromJSON(response.data), 
        message: response.message || 'Job posting updated successfully' 
      };
    } catch (error) {
      console.error('Error updating job:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to update job posting' 
      };
    }
  },

  /**
   * Delete job posting
   * @param {string} id - Job ID
   * @returns {Promise<Object>} - { success, message }
   */
  delete: async (id) => {
    try {
      const response = await apiService.delete(`recruitment/${id}`);
      
      return { 
        success: true, 
        message: response.message || 'Job posting deleted successfully' 
      };
    } catch (error) {
      console.error('Error deleting job:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to delete job posting' 
      };
    }
  },

  /**
   * Change job status
   * @param {string} id - Job ID
   * @param {string} status - New status ('Open', 'Closed', 'On Hold', 'Filled')
   * @returns {Promise<Object>} - { success, data, message }
   */
  updateStatus: async (id, status) => {
    try {
      const response = await apiService.patch(`recruitment/${id}/status`, { status });
      
      return { 
        success: true, 
        data: Recruitment.fromJSON(response.data), 
        message: response.message || `Job status updated to ${status}` 
      };
    } catch (error) {
      console.error('Error updating status:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to update job status' 
      };
    }
  },

  /**
   * Get job applicants
   * @param {string} jobId - Job ID
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} - { success, data, total }
   */
  getApplicants: async (jobId, filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams 
        ? `recruitment/${jobId}/applicants?${queryParams}` 
        : `recruitment/${jobId}/applicants`;
      
      const response = await apiService.get(endpoint);
      
      return { 
        success: true, 
        data: response.data || [], 
        total: response.total || 0 
      };
    } catch (error) {
      console.error('Error fetching applicants:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Shortlist applicant
   * @param {string} jobId - Job ID
   * @param {string} applicantId - Applicant ID
   * @returns {Promise<Object>} - { success, message }
   */
  shortlistApplicant: async (jobId, applicantId) => {
    try {
      const response = await apiService.post(`recruitment/${jobId}/shortlist`, { 
        applicantId 
      });
      
      return { 
        success: true, 
        message: response.message || 'Applicant shortlisted successfully' 
      };
    } catch (error) {
      console.error('Error shortlisting applicant:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to shortlist applicant' 
      };
    }
  },

  /**
   * Hire applicant
   * @param {string} jobId - Job ID
   * @param {string} applicantId - Applicant ID
   * @param {Object} data - Hire details
   * @returns {Promise<Object>} - { success, message }
   */
  hireApplicant: async (jobId, applicantId, data = {}) => {
    try {
      const response = await apiService.post(`recruitment/${jobId}/hire`, { 
        applicantId,
        ...data
      });
      
      return { 
        success: true, 
        message: response.message || 'Applicant hired successfully' 
      };
    } catch (error) {
      console.error('Error hiring applicant:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to hire applicant' 
      };
    }
  },

  /**
   * Reject applicant
   * @param {string} jobId - Job ID
   * @param {string} applicantId - Applicant ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} - { success, message }
   */
  rejectApplicant: async (jobId, applicantId, reason = '') => {
    try {
      const response = await apiService.post(`recruitment/${jobId}/reject`, { 
        applicantId,
        reason
      });
      
      return { 
        success: true, 
        message: response.message || 'Applicant rejected' 
      };
    } catch (error) {
      console.error('Error rejecting applicant:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to reject applicant' 
      };
    }
  },

  /**
   * Get recruitment statistics
   * @param {Object} filters - { department, startDate, endDate }
   * @returns {Promise<Object>} - { success, data }
   */
  getStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams 
        ? `recruitment/statistics?${queryParams}` 
        : 'recruitment/statistics';
      
      const response = await apiService.get(endpoint);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error fetching statistics:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message 
      };
    }
  },

  /**
   * Get open jobs
   * @returns {Promise<Object>} - { success, data, total }
   */
  getOpenJobs: async () => {
    try {
      const response = await apiService.get('recruitment?status=Open');
      const items = response.data?.map(item => Recruitment.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: items.length 
      };
    } catch (error) {
      console.error('Error fetching open jobs:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Get urgent jobs (closing soon)
   * @param {number} days - Days threshold (default: 7)
   * @returns {Promise<Object>} - { success, data, total }
   */
  getUrgentJobs: async (days = 7) => {
    try {
      const response = await apiService.get(`recruitment/urgent?days=${days}`);
      const items = response.data?.map(item => Recruitment.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: items.length 
      };
    } catch (error) {
      console.error('Error fetching urgent jobs:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Export job postings
   * @param {Object} filters - Export filters
   * @param {string} format - 'csv' | 'excel' | 'pdf'
   * @returns {Promise<Object>} - { success, data }
   */
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`recruitment/export?${queryParams}`);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error exporting jobs:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to export job postings' 
      };
    }
  },

  /**
   * Publish job to job board
   * @param {string} id - Job ID
   * @param {Object} platforms - { linkedin, indeed, naukri, etc }
   * @returns {Promise<Object>} - { success, message }
   */
  publishJob: async (id, platforms = {}) => {
    try {
      const response = await apiService.post(`recruitment/${id}/publish`, platforms);
      
      return { 
        success: true, 
        message: response.message || 'Job published successfully' 
      };
    } catch (error) {
      console.error('Error publishing job:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to publish job' 
      };
    }
  },

  /**
   * Clone job posting
   * @param {string} id - Job ID to clone
   * @returns {Promise<Object>} - { success, data, message }
   */
  cloneJob: async (id) => {
    try {
      const response = await apiService.post(`recruitment/${id}/clone`);
      
      return { 
        success: true, 
        data: Recruitment.fromJSON(response.data), 
        message: response.message || 'Job cloned successfully' 
      };
    } catch (error) {
      console.error('Error cloning job:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to clone job' 
      };
    }
  },

  /**
   * Get jobs by department
   * @param {string} department - Department name
   * @returns {Promise<Object>} - { success, data, total }
   */
  getByDepartment: async (department) => {
    try {
      const response = await apiService.get(`recruitment?department=${department}`);
      const items = response.data?.map(item => Recruitment.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: items.length 
      };
    } catch (error) {
      console.error('Error fetching jobs by department:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Search jobs
   * @param {string} query - Search query
   * @returns {Promise<Object>} - { success, data, total }
   */
  search: async (query) => {
    try {
      const response = await apiService.get(`recruitment/search?q=${encodeURIComponent(query)}`);
      const items = response.data?.map(item => Recruitment.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: items.length 
      };
    } catch (error) {
      console.error('Error searching jobs:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Get recruitment pipeline
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} - { success, data }
   */
  getPipeline: async (jobId) => {
    try {
      const response = await apiService.get(`recruitment/${jobId}/pipeline`);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error fetching pipeline:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message 
      };
    }
  },

  /**
   * Schedule interview
   * @param {string} jobId - Job ID
   * @param {string} applicantId - Applicant ID
   * @param {Object} data - Interview details
   * @returns {Promise<Object>} - { success, message }
   */
  scheduleInterview: async (jobId, applicantId, data) => {
    try {
      const response = await apiService.post(`recruitment/${jobId}/interview`, {
        applicantId,
        ...data
      });
      
      return { 
        success: true, 
        message: response.message || 'Interview scheduled successfully' 
      };
    } catch (error) {
      console.error('Error scheduling interview:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to schedule interview' 
      };
    }
  }
};

export default recruitmentController;